import React from 'react';
import { useTranslation } from 'react-i18next';
import { FileText, Download, Share, Eye, File } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { reportTypes } from './reportConstants';

const getFormatIcon = (format) => {
  switch (format) {
    case 'PDF': return <File className="h-4 w-4 text-red-600" />;
    case 'EXCEL': return <File className="h-4 w-4 text-green-600" />;
    default: return <FileText className="h-4 w-4" />;
  }
};

const getReportIcon = (typeId) => {
  const reportType = reportTypes.find((rt) => rt.id === typeId);
  if (reportType) {
    const IconComponent = reportType.icon;
    return <IconComponent className="h-4 w-4" />;
  }
  return <FileText className="h-4 w-4" />;
};

const formatFileSize = (bytes) => {
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return Math.round(bytes / 1024) + ' KB';
  return Math.round(bytes / (1024 * 1024)) + ' MB';
};

const AdvancedReportsListCard = ({ reports, downloadReport, shareReport }) => {
  const { t, i18n } = useTranslation();
  return (
  <Card>
    <CardHeader>
      <CardTitle>{t('reportsComp.listTitle')}</CardTitle>
      <CardDescription>{t('reportsComp.listSubtitle')}</CardDescription>
    </CardHeader>
    <CardContent>
      {reports.length === 0 ? (
        <div className="text-center py-8">
          <FileText className="h-12 w-12 text-muted-foreground/50 mx-auto mb-4" />
          <p className="text-muted-foreground">{t('reportsComp.empty')}</p>
          <p className="text-xs text-muted-foreground mt-1">{t('reportsComp.emptyHint')}</p>
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('reportsComp.colReport')}</TableHead>
              <TableHead>{t('reportsComp.colType')}</TableHead>
              <TableHead>{t('reportsComp.colDate')}</TableHead>
              <TableHead>{t('reportsComp.colFormat')}</TableHead>
              <TableHead>{t('reportsComp.colSize')}</TableHead>
              <TableHead>{t('reportsComp.colStatus')}</TableHead>
              <TableHead className="text-right">{t('reportsComp.colActions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {reports.map((report) => (
              <TableRow key={report.id}>
                <TableCell>
                  <div className="flex items-center space-x-2">
                    {getReportIcon(report.type)}
                    <span className="font-medium">{report.nom}</span>
                  </div>
                </TableCell>
                <TableCell><Badge variant="outline">{report.type}</Badge></TableCell>
                <TableCell>{new Date(report.date_generation).toLocaleString(i18n.language)}</TableCell>
                <TableCell>
                  <div className="flex items-center space-x-1">
                    {getFormatIcon(report.format)}
                    <span>{report.format}</span>
                  </div>
                </TableCell>
                <TableCell>{formatFileSize(report.taille)}</TableCell>
                <TableCell>
                  <Badge variant={report.statut === 'COMPLETED' ? 'default' : 'secondary'}>
                    {report.statut === 'COMPLETED' ? t('reportsComp.completed') : t('reportsComp.inProgress')}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end space-x-1">
                    <Button variant="ghost" size="icon" onClick={() => downloadReport(report.id)}>
                      <Download className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => shareReport(report)}>
                      <Share className="h-4 w-4" />
                    </Button>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="ghost" size="icon"><Eye className="h-4 w-4" /></Button>
                      </PopoverTrigger>
                      <PopoverContent>
                        <div className="space-y-2">
                          <h4 className="font-semibold">{t('reportsComp.detailsTitle')}</h4>
                          <Separator />
                          <div className="space-y-1 text-sm">
                            <p><strong>{t('reportsComp.idLabel')}</strong> {report.id}</p>
                            <p><strong>{t('reportsComp.generatedLabel')}</strong> {new Date(report.date_generation).toLocaleString(i18n.language)}</p>
                            <p><strong>{t('reportsComp.sizeLabel')}</strong> {formatFileSize(report.taille)}</p>
                            {report.url_download && (
                              <p><strong>{t('reportsComp.urlLabel')}</strong> <code className="text-xs">{report.url_download}</code></p>
                            )}
                          </div>
                        </div>
                      </PopoverContent>
                    </Popover>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </CardContent>
  </Card>
  );
};

export default AdvancedReportsListCard;
